import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export type UserRole = "user" | "instructor" | "admin";

// Cache roles to prevent excessive database calls
const rolesCache = new Map<string, { roles: UserRole[], timestamp: number }>();

// Different cache durations based on role sensitivity
// SECURITY: Zero cache for admins to eliminate privilege escalation window
const getCacheDuration = (roles: UserRole[]): number => {
  // Admin roles have ZERO cache for immediate revocation
  // Combined with realtime listener, this ensures instant role invalidation
  if (roles.includes('admin')) {
    return 0; // No cache for admins - always fresh verification
  }
  // Regular users can have longer cache for performance
  return 30 * 1000; // 30 seconds for non-admins
};

export function useUserRole() {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadUserRoles();
    
    // Set up realtime listener for role changes
    const setupRealtimeListener = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase
        .channel('user-roles-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_roles',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            // Clear cache and reload roles when changes detected
            rolesCache.delete(user.id);
            loadUserRoles();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    setupRealtimeListener();
  }, []);

  const loadUserRoles = async () => {
    if (loadingRef.current) return; // Prevent concurrent loads
    loadingRef.current = true;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsLoading(false);
        loadingRef.current = false;
        return;
      }

      // Check cache first with dynamic duration based on roles
      const cached = rolesCache.get(user.id);
      if (cached) {
        const cacheDuration = getCacheDuration(cached.roles);
        if (Date.now() - cached.timestamp < cacheDuration) {
          setRoles(cached.roles);
          setIsLoading(false);
          loadingRef.current = false;
          return;
        }
      }

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) {
        logger.error("Error loading user roles", error);
        setIsLoading(false);
        loadingRef.current = false;
        return;
      }

      if (data) {
        const userRoles = data.map((r) => r.role as UserRole);
        setRoles(userRoles);
        // Update cache
        rolesCache.set(user.id, { roles: userRoles, timestamp: Date.now() });
      }
      
      setIsLoading(false);
    } catch (error) {
      logger.error("Failed to load user roles", error);
      setIsLoading(false);
    } finally {
      loadingRef.current = false;
    }
  };

  const hasRole = (role: UserRole) => roles.includes(role);
  const isAdmin = hasRole("admin");
  const isInstructor = hasRole("instructor");
  const isUser = hasRole("user");

  // SECURITY: For critical operations, bypass cache and verify roles server-side
  // Critical operations: user deletion, role changes, payment operations, security settings
  // Use this method when you need real-time role verification
  const verifyRoleFresh = async (role: UserRole): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Clear cache to force fresh database check
      rolesCache.delete(user.id);
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", role)
        .maybeSingle();

      if (error) {
        logger.error("Error verifying role", error);
        return false;
      }

      return !!data;
    } catch (error) {
      logger.error("Failed to verify role", error);
      return false;
    }
  };

  return {
    roles,
    hasRole,
    isAdmin,
    isInstructor,
    isUser,
    isLoading,
    reloadRoles: loadUserRoles,
    verifyRoleFresh, // Use for critical operations requiring real-time verification
  };
}
